import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { selectAdminToken } from './sessionSlice';
import { apiUrl } from '../utils/apiUrl';

export const fetchThemeData = createAsyncThunk(
  'api/fetchThemeData',
  async (
    { themeId = null, isAdmin = false } = {},
    { getState, rejectWithValue }
  ) => {
    try {
      const state = getState();
      const adminToken = selectAdminToken(state);

      if (!adminToken) {
        return rejectWithValue('Missing session token');
      }

      const response = await fetch(
        apiUrl('/fetchThemeData'),
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${adminToken}`,
          },
          body: JSON.stringify({
            currentTheme: themeId,
          }),
        }
      );

      if (!response.ok) throw new Error('Network response was not ok');
      return await response.json();
    } catch (error) {
      return rejectWithValue(error.message);
    }
  }
);

const persistedTheme = localStorage.getItem('currentTheme');
const safePersistedTheme =
  persistedTheme && persistedTheme !== 'undefined' && persistedTheme !== 'null'
    ? persistedTheme
    : null;

const themeSlice = createSlice({
  name: 'theme',
  initialState: {
    currentTheme: safePersistedTheme,
    availableThemes: [],
    status: 'idle',
    error: null,
    data: null,
    lastRequestId: null,
  },
  reducers: {
    selectTheme: (state, action) => {
      state.currentTheme = action.payload;
      localStorage.setItem('currentTheme', action.payload);
    },
    resetTheme: (state) => {
      state.currentTheme = null;
      localStorage.removeItem('currentTheme');
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchThemeData.pending, (state, action) => {
        state.status = 'loading';
        state.error = null;
        state.lastRequestId = action.meta.requestId;
      })
      .addCase(fetchThemeData.fulfilled, (state, action) => {
        // Ignore stale responses from earlier concurrent requests
        if (state.lastRequestId && action.meta.requestId !== state.lastRequestId) {
          return;
        }
        state.status = 'succeeded';
        state.error = null;
        const data = action.payload?.data ?? null;
        state.data = data;

        const themeName = data?.themename || null;
        if (themeName) {
          state.currentTheme = themeName;
          localStorage.setItem('currentTheme', themeName);
        }

        if (action.meta.arg?.isAdmin) {
          state.availableThemes = action.payload?.availableThemes || [];
        }
      })
      .addCase(fetchThemeData.rejected, (state, action) => {
        // Ignore stale rejections so a failed older call cannot wipe a newer success
        if (state.lastRequestId && action.meta.requestId !== state.lastRequestId) {
          return;
        }
        state.status = 'failed';
        state.error = action.payload;
      });
  },
});

export const { selectTheme, resetTheme } = themeSlice.actions;
export default themeSlice.reducer;
